// ──────────────────────────────────────────────
// DailyCallService Types
// Phase 1 — standalone Daily.co Call Object wrapper
// ──────────────────────────────────────────────

// ── Connection Status ──

export type DailyConnectionStatus =
  | 'idle'
  | 'initializing'
  | 'joining'
  | 'connected'
  | 'reconnecting'
  | 'leaving'
  | 'error';

// ── State (exposed via useSyncExternalStore) ──

export interface DailyCallState {
  status: DailyConnectionStatus;
  /** The Daily room URL currently joined (null when idle) */
  roomUrl: string | null;
  /** Number of participants in the call (including local) */
  participantCount: number;
  /** Whether local audio is enabled */
  localAudioEnabled: boolean;
  /** Whether local video is enabled */
  localVideoEnabled: boolean;
  /** Last error message, if any */
  error: string | null;
  /** Local audio track reference for pipeline integration */
  localAudioTrack: MediaStreamTrack | null;
  /** Remote participant audio tracks keyed by session_id */
  remoteAudioTracks: Map<string, MediaStreamTrack>;
  /** Remote participant display names keyed by session_id */
  remoteParticipantNames: Map<string, string>;
}

// ── Events ──

export interface DailyCallEventMap {
  statusChanged: { status: DailyConnectionStatus; prev: DailyConnectionStatus };
  participantJoined: { sessionId: string };
  participantLeft: { sessionId: string };
  trackStarted: { sessionId: string; type: string; track: MediaStreamTrack };
  trackStopped: { sessionId: string; type: string };
  trackSwapDetected: { previousTrackId: string; newTrackId: string };
  error: { message: string; cause?: unknown };
}

// ── Configuration ──

export interface DailyCallConfig {
  /** Custom audio source (MediaStreamTrack) or device ID */
  audioSource?: MediaStreamTrack | string;
  /** Custom video source (MediaStreamTrack) or device ID */
  videoSource?: MediaStreamTrack | string;
}

// ── Factory ──

export function initialDailyCallState(): DailyCallState {
  return {
    status: 'idle',
    roomUrl: null,
    participantCount: 0,
    localAudioEnabled: false,
    localVideoEnabled: false,
    error: null,
    localAudioTrack: null,
    remoteAudioTracks: new Map(),
    remoteParticipantNames: new Map(),
  };
}
